import style from "./PerfumeOrder.module.scss";
import { useState, useEffect } from "react";
// Mui
import LinearProgress from "@mui/material/LinearProgress";
import { DataGrid } from "@mui/x-data-grid";
// API
import useGetAllDiorSauvageOrdersApi from "../../API/useGetAllDiorSauvageOrdersApi";

export default function PerfumeOrder() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data, isPending: isGetAllOrdersPending, fetchStatus } = useGetAllDiorSauvageOrdersApi();

  // Format phone number for WhatsApp link
  const formatPhoneForWhatsApp = (phone) => {
    if (!phone) return null;

    const cleaned = phone.replace(/\D/g, "");

    let formatted = cleaned;
    if (formatted.startsWith("0")) {
      formatted = "966" + formatted.substring(1);
    }

    if (formatted.length === 9) {
      formatted = "966" + formatted;
    }

    return formatted;
  };

  // Transform API data to match DataGrid rows
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (data?.data) {
      const formatted = data.data.map((item, index, array) => ({
        id: item.id ?? item.order_id ?? index,
        index: array.length - index,
        order_id: item.order_id,
        date: item.created_at,
        time: item.created_at,
        full_name: item.full_name,
        phone: item.phone,
        whatsapp_number: formatPhoneForWhatsApp(item.phone),
        city: item.city,
        payment_method: item.payment_method,
        product: item.product,
        price: item.price,
      }));

      setRows(formatted);
    }
  }, [data]);

  const columns = [
    {
      field: "index",
      headerName: "#",
      width: 70,
      sortable: false,
      headerAlign: "center",
      align: "center",
      disableColumnMenu: true,
    },
    {
      field: "phone",
      headerName: "Phone",
      flex: 1,
      minWidth: 130,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const whatsappNumber = formatPhoneForWhatsApp(params.value);

        if (!whatsappNumber) {
          return <div>{params.value || "N/A"}</div>;
        }

        const whatsappUrl = `https://wa.me/${whatsappNumber}`;

        return (
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#25D366",
              textDecoration: "none",
              fontWeight: "bold",
              padding: "4px 8px",
              borderRadius: "4px",
              backgroundColor: "rgba(37, 211, 102, 0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {params.value}
          </a>
        );
      },
    },
    {
      field: "product",
      headerName: "Product",
      flex: 1,
      minWidth: 150,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "order_id",
      headerName: "Order ID",
      flex: 1,
      minWidth: 130,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      minWidth: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "time",
      headerName: "Time",
      flex: 1,
      minWidth: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => formatTime(params.value),
    },
    {
      field: "full_name",
      headerName: "Name",
      flex: 1,
      minWidth: 175,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (params.value ? params.value : <div>N/A</div>),
    },

    {
      field: "city",
      headerName: "City",
      flex: 1,
      minWidth: 120,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "payment_method",
      headerName: "Payment Method",
      flex: 1,
      minWidth: 150,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },

    {
      field: "price",
      headerName: "Price",
      flex: 1,
      minWidth: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <div
          style={{
            backgroundColor: "#d33030",
            color: "#fff",
            padding: "3px",
          }}
        >
          {params.value}
        </div>
      ),
    },
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    };
    return date.toLocaleDateString("en-GB", options);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const options = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    return date.toLocaleTimeString("en-GB", options);
  };

  // Responsive table
  const [containerWidth, setContainerWidth] = useState(window.innerWidth < 600 ? window.innerWidth - 16 : "100%");

  const updateContainerWidth = () => {
    if (window.innerWidth < 600) {
      setContainerWidth(window.innerWidth - 16);
    } else {
      setContainerWidth("100%");
    }
  };

  useEffect(() => {
    updateContainerWidth();
    window.addEventListener("resize", updateContainerWidth);
    return () => {
      window.removeEventListener("resize", updateContainerWidth);
    };
  }, []);

  return (
    <div className={style.container}>
      {(isGetAllOrdersPending || fetchStatus === "fetching") && (
        <div className={style.progressContainer}>
          <LinearProgress />
        </div>
      )}

      <div
        className={style.datagrid_container}
        style={{
          width: containerWidth,
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          getRowHeight={() => "auto"}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 100 },
            },
          }}
          checkboxSelection={false}
          pageSizeOptions={[10, 25, 50, 100]}
          disableMultipleRowSelection
          disableColumnSort
          disableMultipleColumnSorting
          style={{ width: "100%", height: "100%", overflowX: "auto" }}
          sx={{
            "& .MuiDataGrid-cell": {
              whiteSpace: "normal",
              lineHeight: "1.4",
              py: 1,
              display: "flex",
              alignItems: "center",
            },
          }}
          disableRowSelectionOnClick
          disableVirtualization
        />
      </div>
    </div>
  );
}
